import { IDatabaseLineItem } from "../../pages/CreatorTools/LineItemDBCreationTool/types";
import { ComplementsRuleInputs, ComplementsRuleOverrides, Rule } from "@rebuild/shared";
import { getAllValidPermutations } from "../getAllValidPermutations";
import { normalizeString } from "../utils";
import { ComplementsRuleInputPaths, resolveArrayPath, ValidationError, ValidationErrorEntry } from "./types";

type ComplementsRule = Rule & { templateId: "complements_and_recommendations" };
type ValidationResult = { path: string; validationError: ValidationError; isOverride?: boolean };

// --- Parent checks ---

function checkParentLineItemMissing(rule: ComplementsRule): ValidationResult | undefined {
	if (!rule.ruleInputs.parent?.lineItemId) {
		return { path: "parent", validationError: ValidationError.ParentLineItemNotSelected };
	}
}

function checkParentLineItemInvalid(rule: ComplementsRule, dbLineItems: IDatabaseLineItem[]): ValidationResult | undefined {
	const id = rule.ruleInputs.parent?.lineItemId;
	if (id && !dbLineItems.some((li) => li.id === id)) {
		return { path: "parent", validationError: ValidationError.InvalidParentLineItem };
	}
}

function checkParentLineItemPermutationInvalid(rule: ComplementsRule, dbLineItems: IDatabaseLineItem[]): ValidationResult | undefined {
	const { lineItemId: id, selectedPermutation: permutation } = rule.ruleInputs.parent ?? {};
	if (!id || !permutation) return undefined;
	const dbLineItem = dbLineItems.find((li) => li.id === id);
	if (!dbLineItem) return undefined; // already caught by checkParentLineItemInvalid
	if (!getAllValidPermutations(dbLineItem.elements).includes(normalizeString(permutation))) {
		return { path: "parent", validationError: ValidationError.InvalidParentLineItemPermutation };
	}
}

// --- Child checks ---

function checkChildLineItemMissing(rule: ComplementsRule): ValidationResult | undefined {
	if (!rule.ruleInputs.child?.lineItem?.lineItemId) {
		return { path: "child.lineItem", validationError: ValidationError.ChildLineItemNotSelected };
	}
}

/**
 * We can only override existing line items with a recommendation/complement IF that new line item is a complement; it doesn't make
 * sense to have a recommendation to override an existing line item. If both of these flags are set, then the user probably made a
 * mistake when creating this rule. 
 */
function checkChildIsLikelyAndOverrideExistingLineItem(rule: ComplementsRule): ValidationResult | undefined {
	const { child } = rule.ruleInputs;
	if (child?.isLikely && child?.overrideExistingLineItem) {
		return { path: "child.overrideExistingLineItem", validationError: ValidationError.IsLikelyAndOverrideExistingLineItem };
	}
}

function checkChildLineItemInvalid(rule: ComplementsRule, dbLineItems: IDatabaseLineItem[]): ValidationResult | undefined {
	const id = rule.ruleInputs.child?.lineItem?.lineItemId;
	if (id && !dbLineItems.some((li) => li.id === id)) {
		return { path: "child.lineItem", validationError: ValidationError.InvalidChildLineItem };
	}
}

function checkChildLineItemPermutationInvalid(rule: ComplementsRule, dbLineItems: IDatabaseLineItem[]): ValidationResult | undefined {
	const { lineItemId: id, selectedPermutation: permutation } = rule.ruleInputs.child?.lineItem ?? {};
	if (!id || !permutation) return undefined;
	const dbLineItem = dbLineItems.find((li) => li.id === id);
	if (!dbLineItem) return undefined; // already caught by checkChildLineItemInvalid
	if (!getAllValidPermutations(dbLineItem.elements).includes(normalizeString(permutation))) {
		return { path: "child.lineItem", validationError: ValidationError.InvalidChildLineItemPermutation };
	}
}

// --- Override checks ---

function checkOverrideChildLineItemMissing(override: ComplementsRuleOverrides, index: number): ValidationResult | undefined {
	if (!override.doNotCreateChild && !override.child?.lineItem?.lineItemId) {
		return {
			path: resolveArrayPath<ComplementsRuleInputs>("overrides[number].child.lineItem", index),
			validationError: ValidationError.ChildLineItemNotSelected,
			isOverride: true,
		};
	}
}

function checkOverrideIsLikelyAndOverrideExistingLineItem(override: ComplementsRuleOverrides, index: number): ValidationResult | undefined {
	if (override.child?.isLikely && override.child?.overrideExistingLineItem) {
		return {
			path: resolveArrayPath<ComplementsRuleInputs>("overrides[number].child.overrideExistingLineItem", index),
			validationError: ValidationError.IsLikelyAndOverrideExistingLineItem,
			isOverride: true,
		};
	}
}

function checkOverrideChildLineItemInvalid(override: ComplementsRuleOverrides, index: number, dbLineItems: IDatabaseLineItem[]): ValidationResult | undefined {
	const id = override.child?.lineItem?.lineItemId;
	if (id && !dbLineItems.some((li) => li.id === id)) {
		return {
			path: resolveArrayPath<ComplementsRuleInputs>("overrides[number].child.lineItem", index),
			validationError: ValidationError.InvalidChildLineItem,
			isOverride: true,
		};
	}
}

function checkOverrideChildLineItemPermutationInvalid(
	override: ComplementsRuleOverrides,
	index: number,
	dbLineItems: IDatabaseLineItem[],
): ValidationResult | undefined {
	const { lineItemId: id, selectedPermutation: permutation } = override.child?.lineItem ?? {};
	if (!id || !permutation) return undefined;
	const dbLineItem = dbLineItems.find((li) => li.id === id);
	if (!dbLineItem) return undefined; // already caught by checkOverrideChildLineItemInvalid
	if (!getAllValidPermutations(dbLineItem.elements).includes(normalizeString(permutation))) {
		return {
			path: resolveArrayPath<ComplementsRuleInputs>("overrides[number].child.lineItem", index),
			validationError: ValidationError.InvalidChildLineItemPermutation,
			isOverride: true,
		};
	}
}

export function validateComplementsAndRecommendations(
	rule: ComplementsRule,
	dbLineItems: IDatabaseLineItem[],
): Map<ComplementsRuleInputPaths, ValidationErrorEntry> | undefined {
	const results: ValidationResult[] = [
		checkParentLineItemMissing(rule),
		checkParentLineItemInvalid(rule, dbLineItems),
		checkParentLineItemPermutationInvalid(rule, dbLineItems),
		checkChildLineItemMissing(rule),
		checkChildIsLikelyAndOverrideExistingLineItem(rule),
		checkChildLineItemInvalid(rule, dbLineItems),
		checkChildLineItemPermutationInvalid(rule, dbLineItems),
		...(rule.ruleInputs.overrides ?? []).flatMap((override, i) => [
			checkOverrideChildLineItemMissing(override, i),
			checkOverrideIsLikelyAndOverrideExistingLineItem(override, i),
			checkOverrideChildLineItemInvalid(override, i, dbLineItems),
			checkOverrideChildLineItemPermutationInvalid(override, i, dbLineItems),
		]),
	].filter((r): r is ValidationResult => r !== undefined);

	if (results.length === 0) return undefined;

	return new Map(
		results.map(({ path, validationError, isOverride }) => [
			path as ComplementsRuleInputPaths,
			{ validationError, ...(isOverride ? { isInOverride: true } : {}) } satisfies ValidationErrorEntry,
		]),
	);
}
